import axios from '../config/api'
import Vue from 'vue'
import moment from 'moment'
import { orderDetailTransform } from '@/transforms/orderTransform'
import MERCURE_TOPIC from '../config/mercure'

const campaignType = process.env.VUE_APP_CAMPAIGN_TYPE ?? 'BNN'

const state = () => ({
  data: [],
  eventSources: {},
  payload: null,
  isError: false,
  isExpired: false,
  isLoading: false,
  submit: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
})

// getters
const getters = {
  total: state => state.data.total_price || 0,
  orderDetail: state => state.data,
  orderStatus: state => state.data.status || '',
  isLoading: state => key => state[key].isLoading || false,
  isSuccess: state => key => state[key].isSuccess || false,
  isError: state => key => state[key].isError || false,
  errorMessage: state => key => state[key].errorMessage || '',
}

// actions
const actions = {
  async getOrder({ commit }, payload) {
    commit('setOrder')
    await axios
      .get(`/orders/${payload}`)
      .then(response => {
        commit('setOrderSuccess', response.data.data)
      })
      .catch(() => {
        commit('setOrderError')
      })
  },

  /* Subscribe to order status updates with Mercure SSE */
  subscribeOrderStatus({ commit, state }, uuid) {
    const hubUrl = process.env.VUE_APP_HUB_MERCURE_URL
    const env =
      process.env.NODE_ENV !== 'production' ? 'dev' : 'production'
    const topic = `${env}.${MERCURE_TOPIC.ORDER_STATUS}.${uuid}`

    const url = `${hubUrl}?topic=${topic}`

    const es = new EventSource(url)

    state.eventSources[uuid] = es

    state.eventSources[uuid].onmessage = event => {
      const data = JSON.parse(event.data)

      if (data.data.status) {
        commit('setOrderStatusSuccess', data.data.status)
      }
    }
  },
  unsubscribeOrderStatus({ state }, uuid) {
    if (uuid && state.eventSources[uuid]) {
      state.eventSources[uuid].close()
      delete state.eventSources[uuid]
    }
  },

  async getOrderLists({ commit }) {
    const headers = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('tokenOTP')}`,
      },
    }

    commit('getOrderListsRequest')
    await axios
      .get(`/user/order-history/${campaignType}`, headers)
      .then(response => {
        commit('getOrderListsRequestSuccess', response.data.data)
      })
      .catch(() => {
        commit('getOrderListsRequestFailure')
        localStorage.removeItem('tokenOTP')
      })
  },

  getOrderDetail({ commit }, payload) {
    commit('setOrder')
    axios
      .get(`/user/order-detail/${payload}`)
      .then(response => {
        commit('setOrderSuccess', response.data.data)
      })
      .catch(() => {
        commit('setOrderError')
      })
    // }
  },

  async extendTimePayAtStore({ commit }, payload) {
    commit('submitRequest')
    await axios
      .post('/user/extend-pay-at-store', payload)
      .then(() => {
        commit('submitRequestSuccess', null)

        Vue.$toast.success('ขยายเวลาชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        commit('submitRequestFailure')

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },

  isPayAtStoreExpired({ commit }) {
    commit('setOrderExpired')
  },

  async confirmPayment({ commit }, payload) {
    commit('submitRequest')

    const accessToken = Vue.prototype.$cookies.get('token')
    // localStorage.getItem('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    await axios
      .post('/orders/stamp-pay-at-store', payload, {
        headers: {
          Authorization: `Bearer ' ${accessToken}`,
        },
      })
      .then(() => {
        commit('submitRequestSuccess', null)

        Vue.$toast.success('ยืนยันการชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        commit('submitRequestFailure')

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },

  async inquiryTransactionRequest({ commit }, uuid) {
    commit('submitRequest')

    const transformedPayload = {
      order_id: uuid,
    }

    await axios
      .post('/orders/inquiry', transformedPayload)
      .then(res => {
        const transformedData = {
          message: res.data.data.message,
        }

        commit('submitRequestSuccess', transformedData)
      })
      .catch(error => {
        commit('submitRequestFailure', error.response.data.message)

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },
}

// mutations
const mutations = {
  setOrder(state) {
    state.isLoading = true
    state.isError = false
  },
  setOrderSuccess(state, payload) {
    state.isLoading = false
    state.data = orderDetailTransform(payload)
    state.isError = false
  },
  setOrderError(state) {
    state.isLoading = false
    state.isError = true
  },
  setOrderStatusSuccess(state, payload) {
    state.data = {
      ...state.data,
      status: payload,
    }
  },

  getOrderListsRequest(state) {
    state.isLoading = true
    state.isError = false
  },
  getOrderListsRequestSuccess(state, payload) {
    state.isLoading = false
    state.data = payload
    state.isError = false
  },
  getOrderListsRequestFailure(state) {
    state.isLoading = false
    state.isError = true
  },
  submitRequest(state) {
    state.submit.isLoading = true
    state.submit.isError = false
    state.submit.isSuccess = false
  },
  submitRequestSuccess(state) {
    state.submit.isLoading = false
    state.submit.isSuccess = true
  },
  submitRequestFailure(state) {
    state.submit.isLoading = false
    state.submit.isError = true
  },
  resetSubmit(state) {
    state.submit.isLoading = false
    state.submit.isError = false
    state.submit.isSuccess = false
  },
  setOrderExpired(state) {
    state.isExpired =
      moment().isAfter(
        moment.unix(state.data.pay_at_store_expire_at_timestamp_bkk),
      ) ?? false
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
